import { internalAxios } from '@/plugins/vueAxios'
import { defineStore } from 'pinia'
import type { GalleryPack, OpenedPack } from '@/interfaces/gallery/Packs'
import type { PacksResponse, OpenPackResponse, PiecesReceived } from '@/interfaces/responses/gallery/GalleryPacksApiResponse'
import {
  galleryPacksEndpoint as GALLERY_PACKS_ENDPOINT,
  galleryPackBuyEndPoint as GALLERY_PACK_BUY_ENDPOINT,
  galleryPackOpenEndpoint as GALLERY_PACK_OPEN_ENDPOINT,
  premiumAddonEndpoint as PREMIUM_ENDPOINT
} from '@/globalVariables'
import type { PremiumAddon } from '@/interfaces/premium/Offer'
import type { PremiumPack } from '@/interfaces/premium/PremiumPack'
import { GalleryPackType } from '@/interfaces/gallery/enums'

interface GalleryPacksState {
  packs: GalleryPack[] | null
  premiumPacks: PremiumPack[] | null
  receivedPieces: PiecesReceived | null
  openedPackMessage: OpenedPack | null
}

export const useGalleryPacksStore = defineStore('galleryPacks', {
  state: (): GalleryPacksState => ({
    packs: null,
    premiumPacks: null,
    receivedPieces: null,
    openedPackMessage: null
  }),

  actions: {
    async loadPacks(): Promise<void> {
      this.packs = null

      try {
        const data = await internalAxios.get<{}, PacksResponse>(
          GALLERY_PACKS_ENDPOINT,
        )

        if (!data) return

        this.packs = data.packs.map((pack: GalleryPack): GalleryPack => {
          return {
            ...pack,
            pack_type: pack.pack_name.split('_')[2]
          }
        })
      } catch (error: unknown) {
        console.error(error)
      }
    },

    async loadPremiumData(): Promise<void> {
      this.premiumPacks = null

      try {
        const data = await internalAxios.get<{}, PremiumAddon>(PREMIUM_ENDPOINT)
        if (!data || !data.availableOffers) return

        this.premiumPacks = data.availableOffers.filter(
          (a: PremiumPack): boolean => 
            a.parameters.type === GalleryPackType.Regular ||
            a.parameters.type === GalleryPackType.Premium ||
            a.parameters.type === GalleryPackType.Jumbo
        )

      } catch (error: unknown) {
        console.error(error)
      }
    },

    async openPack(shop_item_id: number, amount: number = 1): Promise<void> {
      this.receivedPieces = null
      this.openedPackMessage = null

      try {
        const response = await internalAxios.put<{}, OpenPackResponse, { shop_item_id: number, amount: number }>(
          GALLERY_PACK_OPEN_ENDPOINT,
          { shop_item_id, amount }
        )

        if (!response || !response.item) return

        // triggers popup message
        this.openedPackMessage = {
          packType: response.item.name,
          packTypeShorthand: (response.item.name as string).split('_')[2],
          amount,
          pieces: this.receivedPieces
        }
      } catch (error: unknown) {
        console.error(error)
      }
    },

    async buyPack(offer_id: number, amount: number, packName: GalleryPackType): Promise<void> {
      try {
        const response: boolean = await internalAxios.post<{}, boolean>(GALLERY_PACK_BUY_ENDPOINT, { offer_id })
        if (!response) return
      } catch (error: unknown) {
        console.error(error)
      }
    },

    saveReceived(received: PiecesReceived): void {
      this.receivedPieces = received
    },

    hideMessage(): void {
      this.openedPackMessage = null
    },

    // update owned pack count
    updatePacks (galleryPackType: GalleryPackType, newAmount: number): void {
      this.packs = this.packs.map((pack: GalleryPack): GalleryPack => {
        if (pack.pack_name !== galleryPackType) { return pack }
        return {
          ...pack,
          amount: newAmount
        }
      })
    }
  }
})
